import type { Game, GameLicense, UserRole } from 'shared/modelTypes/index';
import { Avatar } from './Avatar';
import { Button } from './Button';
import { Plus } from 'lucide-static';
import { Icon } from './Icon';
import { InlineCode } from './InlineCode';
import { idToQr, lateinit } from 'shared/util';
import { render } from 'engine/index';

type LicenseEntry = { license: GameLicense; game: Game };

type GameLicenseListProps = {
	licenses: LicenseEntry[];
	users: Record<string, string>;
	role: UserRole;
};

const filterSelectClasses =
	'block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-theme-600 dark:bg-gray-900 dark:text-gray-100 dark:ring-gray-700 dark:focus:ring-theme-700/80 sm:text-sm sm:leading-6';

export class GameLicenseList extends Base.Component<GameLicenseListProps> {
	tbody: HTMLElement = lateinit;
	emptyMessage: HTMLElement = lateinit;
	selectedUserId: string = '';
	selectedGameKind: string = '';

	override render(): HTMLElement {
		const { licenses, role } = this.props;

		const gameKindMap = new Map<string, string>();
		for (const { game } of licenses) {
			if (game && !gameKindMap.has(game.kind)) {
				gameKindMap.set(game.kind, game.name ?? game.kind);
			}
		}
		const gameOptions = [...gameKindMap.entries()].sort((a, b) => a[1].localeCompare(b[1]));

		const userIds = new Set<string>();
		for (const { license } of licenses) {
			if (license.userId) userIds.add(license.userId);
		}
		const userOptions = [...userIds]
			.map((id) => ({ id, name: this.props.users[id] || id }))
			.sort((a, b) => a.name.localeCompare(b.name));

		const view: HTMLElement = (
			<div class="flex flex-col gap-3">
				<div class="flex gap-4">
					<h2 class="text-gray-700 dark:text-gray-300">Game Licenses</h2>
					{role === 'admin' && (
						<Button size="sm" href="/dashboard/licenses/new">
							New License
							<Icon icon={Plus} class="size-4 text-theme-100" />
						</Button>
					)}
				</div>
				<div class="max-w-[800px]">
					{!licenses.length && <small>No Licenses.</small>}
					{!!licenses.length && (
						<div class="flex flex-col gap-3">
							<div class="flex flex-wrap items-end gap-3">
								{role === 'admin' && (
									<label class="flex flex-col gap-1 text-sm">
										<span class="font-medium text-gray-700 dark:text-gray-300">Gamemaster</span>
										<select
											class={filterSelectClasses}
											on:change={(e: Event) => {
												this.selectedUserId = (e.target as HTMLSelectElement).value;
												this.updateRows();
											}}
										>
											<option value="">All gamemasters</option>
											{userOptions.map((u) => (
												<option value={u.id}>{u.name}</option>
											))}
										</select>
									</label>
								)}
								<label class="flex flex-col gap-1 text-sm">
									<span class="font-medium text-gray-700 dark:text-gray-300">Game</span>
									<select
										class={filterSelectClasses}
										on:change={(e: Event) => {
											this.selectedGameKind = (e.target as HTMLSelectElement).value;
											this.updateRows();
										}}
									>
										<option value="">All games</option>
										{gameOptions.map(([kind, name]) => (
											<option value={kind}>{name}</option>
										))}
									</select>
								</label>
							</div>
							<small empty-message class="hidden text-gray-600 dark:text-gray-400">
								No licenses match the selected filters.
							</small>
							<table class="min-w-full border-collapse overflow-hidden rounded-md bg-white text-sm shadow-md dark:bg-gray-800">
								<thead>
									<tr class="border-b border-gray-200 text-gray-800 dark:border-gray-700 dark:text-gray-100">
										<th class="p-3 text-left"></th>
										<th class="p-3 text-left">Game</th>
										<th class="p-3 text-left">Name</th>
										{role === 'admin' && <th class="p-3 text-left">User</th>}
										<th class="p-3 text-left">License Code</th>
									</tr>
								</thead>
								<tbody license-tbody></tbody>
							</table>
						</div>
					)}
				</div>
			</div>
		);

		this.tbody = view.querySelector('[license-tbody]') as HTMLElement;
		this.emptyMessage = view.querySelector('[empty-message]') as HTMLElement;

		if (licenses.length) {
			this.updateRows();
		}

		return view;
	}

	updateRows() {
		const { licenses, role, users } = this.props;
		const filtered = licenses.filter(({ license, game }) => {
			if (this.selectedUserId && license.userId !== this.selectedUserId) return false;
			if (this.selectedGameKind && game.kind !== this.selectedGameKind) return false;
			return true;
		});

		if (!filtered.length) {
			this.emptyMessage.classList.remove('hidden');
		} else {
			this.emptyMessage.classList.add('hidden');
		}

		const rows = filtered.map(({ license, game }) => (
			<tr
				data-href={`/dashboard/licenses/${license._key}/`}
				tabIndex={0}
				role="link"
				class="cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
			>
				<td class="border-gray-200 p-3">
					<Avatar src={`/images/${game.kind}.jpg`} alt="game icon" />
				</td>
				<td class="border-gray-200 p-3">{game.name}</td>
				<td class="border-gray-200 p-3">{license.name}</td>
				{role === 'admin' && (
					<td class="border-gray-200 p-3">
						<InlineCode>{users[license.userId] || license.userId}</InlineCode>
					</td>
				)}
				<td class="border-gray-200 p-3">
					<InlineCode>{idToQr(license.code ? license.code : parseInt(license._key!))}</InlineCode>
				</td>
			</tr>
		));

		render(this.tbody, rows);
	}
}
